import { Button, Flex, Modal, Switch, Typography, message } from 'antd';
import './index.scss';
import React, { useState } from 'react';
import { Consultant } from '@/types';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { SvgIcon } from '@/components/icon';
import { formatDate } from '@/helpers/dateFormat';
import { updateUser } from '@/api/admin';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import LegalDocumentsSection from './LegalDocumentsSection';
import { useUserLegalAcceptances } from '@/hooks/useLegalDocuments';

interface Props {
  consultant: Consultant;
  rating: string | number;
  totalReviews?: number;
}

export default function ConsultantDetailed({
  consultant,
  rating,
  totalReviews,
}: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const domain = window.location.origin;
  const queryClient = useQueryClient();

  // TCX-669: legal acceptance audit trail for this consultant.
  const {
    data: legalHistory,
    isLoading: legalLoading,
    isError: legalError,
  } = useUserLegalAcceptances(consultant?.id);

  const profilePath = consultant.profileUrl?.startsWith('/consultants/')
    ? consultant.profileUrl
    : `/consultants/${consultant.profileUrl || consultant.id}`;
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const consultantMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateUser({ id, data }),

    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: ['consultant', 'detail', id],
      });

      const prev = queryClient.getQueryData(['consultant', 'detail', id]);

      queryClient.setQueryData(
        ['consultant', 'detail', id],
        (old: Record<string, string>) =>
          old
            ? {
                ...old,
                ...data,
              }
            : old,
      );

      return { prev };
    },

    onError: (_error, variables, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['consultant', 'detail', variables.id], ctx.prev);
      }

      if (variables.data.isProMember !== undefined) {
        message.error('Failed to update Trusted Pro status');
      } else if (variables.data.isNewToTCX !== undefined) {
        message.error('Failed to update New to TCX status');
      } else if (variables.data.isEmergingPro !== undefined) {
        message.error('Failed to update Emerging Pro status');
      } else {
        message.error('Failed to update consultant status');
      }
    },

    onSettled: (_res, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['consultant'] });
    },

    onSuccess: (_data, variables) => {
      if (variables.data.isProMember !== undefined) {
        message.success(
          `Trusted Pro ${
            variables.data.isProMember ? 'activated' : 'deactivated'
          } successfully`,
        );
      } else if (variables.data.isNewToTCX !== undefined) {
        message.success(
          `${
            variables.data.isNewToTCX
              ? 'Marked as New to TCX'
              : 'Removed from New to TCX'
          }.`,
        );
      } else if (variables.data.isEmergingPro !== undefined) {
        message.success(
          `${
            variables.data.isEmergingPro
              ? 'Marked as Emerging Pro'
              : 'Removed from Emerging Pro'
          }.`,
        );
      }
    },
  });

  const renderCalendars = () => {
    if (!consultant.calendars || consultant.calendars.length === 0) {
      return <div>Not connected</div>;
    }

    switch (consultant.calendars[0].provider_name) {
      case 'google':
        return (
          <Flex gap={8} align={'center'}>
            <SvgIcon type={'google'} width={20} height={20} />
            <div>Google</div>
          </Flex>
        );
      case 'office365':
        return (
          <Flex gap={8} align={'center'}>
            <SvgIcon type={'office365'} width={20} height={20} />
            <div>Office 365</div>
          </Flex>
        );
      default:
        return <div>{consultant.calendars[0].provider_name}</div>;
    }
  };

  return (
    <React.Fragment>
      <div className={'detailed-wrapper'}>
        <div className={'detailed-content'}>
          <Typography.Title level={3} style={{ margin: '0 0 24px' }}>
            General Info
          </Typography.Title>
          <div className={'details-sections'}>
            <div className={'details-section'}>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  First Name
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.firstName}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Last Name
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.lastName}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Email
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.email}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Phone
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.phone ? consultant.phone : '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Calendar
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {renderCalendars()}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Rating
                </Typography.Text>
                <Flex align="center" gap={6}>
                  <SvgIcon type={'goldStar'} width={16} height={16} />
                  <Typography.Text className={'general-info-value'}>
                    {rating}
                    {totalReviews && totalReviews > 0
                      ? ` (${totalReviews} ${
                          totalReviews === 1 ? 'review' : 'reviews'
                        })`
                      : ''}
                  </Typography.Text>
                </Flex>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Account Status
                </Typography.Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: consultant.isVerified
                        ? '#1C8271'
                        : '#F68B8B',
                    }}
                  />
                  <Typography.Text>
                    {consultant.isVerified ? 'Active' : 'Inactive'}
                  </Typography.Text>
                </div>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  New to TCX
                </Typography.Text>
                <Switch
                  checked={!!consultant.isNewToTCX}
                  onChange={(checked) =>
                    consultantMutation.mutate({
                      id: consultant.id,
                      data: { isNewToTCX: checked },
                    })
                  }
                  style={{
                    backgroundColor: consultant.isNewToTCX
                      ? '#19A4D4'
                      : '#B0B0B0',
                  }}
                />
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  TCX Trusted Pro
                </Typography.Text>
                <Switch
                  checked={!!consultant.isProMember}
                  onChange={(checked) =>
                    consultantMutation.mutate({
                      id: consultant.id,
                      data: { isProMember: checked },
                    })
                  }
                  style={{
                    backgroundColor: consultant.isProMember
                      ? '#F59F0B'
                      : '#B0B0B0',
                  }}
                />
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Emerging Pro
                </Typography.Text>
                <Switch
                  checked={!!consultant.isEmergingPro}
                  onChange={(checked) =>
                    consultantMutation.mutate({
                      id: consultant.id,
                      data: { isEmergingPro: checked },
                    })
                  }
                  style={{
                    backgroundColor: consultant.isEmergingPro
                      ? '#1C8271'
                      : '#B0B0B0',
                  }}
                />
              </div>
              {consultant.tokenActivation && (
                <React.Fragment>
                  <div className={'details-section-line'}>
                    <Typography.Text className={'general-info-text'}>
                      Activation Link
                    </Typography.Text>
                    <Typography.Text className={'general-info-value'}>
                      <Link
                        to={`${domain}/activate-profile?token=${consultant.tokenActivation}`}
                        target={'_blank'}
                        rel={'noreferrer'}
                      >
                        Activate
                      </Link>
                    </Typography.Text>
                  </div>
                  <div className={'details-section-line'}>
                    <Typography.Text className={'general-info-text'}>
                      Activation Expiration
                    </Typography.Text>
                    <Typography.Text className={'general-info-value'}>
                      {format(
                        new Date(consultant?.tokenActivationExpiresAt || ''),
                        'd MMM, yyyy, h:mm a',
                      )}
                    </Typography.Text>
                  </div>
                </React.Fragment>
              )}
            </div>
            <div className={'details-section'}>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Photo
                </Typography.Text>
                <img
                  src={consultant.profilePhoto}
                  alt={'Logo'}
                  width={24}
                  height={24}
                  style={{ borderRadius: '50%' }}
                />
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Group
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {/* TCX-507: groups are {id,name} refs */}
                  {consultant.groups?.map((g) => g.name).join(', ') || '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Company
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.company ? consultant.company : '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Experience
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.experienceLevel
                    ? `${consultant.experienceLevel} years`
                    : '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Rate
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant.rate} $/15m
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Bio
                </Typography.Text>
                <Button
                  type={'link'}
                  onClick={showModal}
                  className={'bio-button'}
                >
                  See Bio
                </Button>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Groups
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {/* TCX-507: groups are {id,name} refs */}
                  {consultant?.groups?.map((g) => g.name).join(', ') || '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Country
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant?.country?.trim()
                    ? consultant.country
                        .trim()
                        .split(' ')
                        .map((word) => word[0]?.toUpperCase() + word?.slice(1))
                        .join(' ')
                    : '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  City
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant?.city?.trim()
                    ? consultant.city
                        .trim()
                        .split(' ')
                        .map((word) => word[0]?.toUpperCase() + word?.slice(1))
                        .join(' ')
                    : '-'}
                </Typography.Text>
              </div>
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Address
                </Typography.Text>
                <Typography.Text className={'general-info-value'}>
                  {consultant?.address?.trim()
                    ? consultant.address
                        .trim()
                        .split(',')[0]
                        .split(' ')
                        .map((word) => word[0]?.toUpperCase() + word?.slice(1))
                        .join(' ')
                    : '-'}
                </Typography.Text>
              </div>
              {
                <div className={'details-section-line'}>
                  <Typography.Text className={'general-info-text'}>
                    Profile Link
                  </Typography.Text>
                  <Typography.Text className={'general-info-value'}>
                    <Link to={profilePath} target={'_blank'} rel={'noreferrer'}>
                      Visit Profile
                    </Link>
                  </Typography.Text>
                </div>
              }
              <div className={'details-section-line'}>
                <Typography.Text className={'general-info-text'}>
                  Last Update
                </Typography.Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography.Text className={'general-info-value'}>
                    {(consultant?.consultantProfileUpdateTimeStamp &&
                      formatDate(
                        new Date(consultant.consultantProfileUpdateTimeStamp),
                      )) ||
                      (consultant.createdAt &&
                        formatDate(new Date(consultant.createdAt)))}
                  </Typography.Text>
                </div>
              </div>
            </div>
            <LegalDocumentsSection
              history={legalHistory}
              loading={legalLoading}
              isError={legalError}
              showUserColumn={false}
            />
          </div>
        </div>
        {/* <div className="detailed-footer">
          <Link to={'#'} className="detailed-footer-link-wrapper">
            <div className="icon-link">
              <SvgIcon type="mail" width={14} height={14} />
            </div>
            <Typography.Text className="general-info-text">
              Mail
            </Typography.Text>
          </Link>
          <Link to={'#'} className="detailed-footer-link-wrapper">
            <div className="icon-link">
              <SvgIcon type="linkedin" width={14} height={14} />
            </div>

            <Typography.Text className="general-info-text">
              LinedIn
            </Typography.Text>
          </Link>
          <Link to={'#'} className="detailed-footer-link-wrapper">
            <div className="icon-link">
              <SvgIcon type="instagram" width={14} height={14} />
            </div>
            <Typography.Text className="general-info-text">
              Instagram
            </Typography.Text>
          </Link>
          <Link to={'#'} className="detailed-footer-link-wrapper">
            <div className="icon-link">
              <SvgIcon type="facebook" width={14} height={14} />
            </div>
            <Typography.Text className="general-info-text">
              Facebook
            </Typography.Text>
          </Link>
        </div> */}
      </div>

      <Modal
        title={'Consultant Bio'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={null}
        width={400}
      >
        <div className={'modal-section-line'}>
          <Typography.Text className={'general-info-text'}>
            Text
          </Typography.Text>
          <Typography.Text className={'general-info-value'}>
            {consultant.bio}
          </Typography.Text>
        </div>
      </Modal>
    </React.Fragment>
  );
}
